import * as React from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import UnitIconFrame, { resolveUnitLevelBadge } from './UnitIconFrame'

const PICKER_FAVORITES_KEY = 'castleDefensePickerFavorites'

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

const toStringValue = (value, fallback = '') =>
    [undefined, null].includes(value) ? fallback : String(value)

const normalizeSearchText = value =>
    String(value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

const createKeepEntry = () => ({
    id: '',
    amount: '',
    itemType: 'troop',
    label: '',
    statLine: '',
    level: ''
})

const normalizeKeepEntry = value => {
    if (Array.isArray(value))
        return {
            ...createKeepEntry(),
            id: toStringValue(value[0]),
            amount: toStringValue(value[1])
        }

    return {
        id: toStringValue(value?.id ?? value?.value),
        amount: toStringValue(value?.amount),
        itemType: value?.itemType === 'troop' || value?.itemType === 'tool' ? value.itemType : 'troop',
        label: toStringValue(value?.label),
        statLine: toStringValue(value?.statLine),
        level: toStringValue(value?.level)
    }
}

const DEFAULT_LOCATION_SETTINGS = {
    attackThresholdMinutes: '60',
    troopSavingDurationHours: '12',
    troopAttackThreshold: '1',
    minMightPoints: '0',
    minCastleTroopCount: '1',
    useFeathers: true,
    useReserveCommander: false,
    minimumSendAmount: '1',
    reserveCommanderWhiteList: ''
}

const createLocationState = () => ({
    ...DEFAULT_LOCATION_SETTINGS,
    sendTroopsEnabled: false,
    autoPullBackTroops: false,
    keepTroops: [],
    sendToX: '0',
    sendToY: '0',
    openGate: false
})

const normalizeLocationState = (value, fallback = {}) => {
    const locationFallback = {
        ...DEFAULT_LOCATION_SETTINGS,
        ...fallback
    }

    return {
        attackThresholdMinutes: toStringValue(value?.attackThresholdMinutes ?? locationFallback.attackThresholdMinutes, DEFAULT_LOCATION_SETTINGS.attackThresholdMinutes),
        troopSavingDurationHours: toStringValue(value?.troopSavingDurationHours ?? locationFallback.troopSavingDurationHours, DEFAULT_LOCATION_SETTINGS.troopSavingDurationHours),
        troopAttackThreshold: toStringValue(value?.troopAttackThreshold ?? locationFallback.troopAttackThreshold, DEFAULT_LOCATION_SETTINGS.troopAttackThreshold),
        minMightPoints: toStringValue(value?.minMightPoints ?? locationFallback.minMightPoints, DEFAULT_LOCATION_SETTINGS.minMightPoints),
        minCastleTroopCount: toStringValue(value?.minCastleTroopCount ?? locationFallback.minCastleTroopCount, DEFAULT_LOCATION_SETTINGS.minCastleTroopCount),
        useFeathers: Boolean(value?.useFeathers ?? locationFallback.useFeathers),
        useReserveCommander: Boolean(value?.useReserveCommander ?? locationFallback.useReserveCommander),
        minimumSendAmount: toStringValue(value?.minimumSendAmount ?? locationFallback.minimumSendAmount, DEFAULT_LOCATION_SETTINGS.minimumSendAmount),
        reserveCommanderWhiteList: toStringValue(value?.reserveCommanderWhiteList ?? value?.reserveCommanders ?? locationFallback.reserveCommanderWhiteList, DEFAULT_LOCATION_SETTINGS.reserveCommanderWhiteList),
        sendTroopsEnabled: Boolean(value?.sendTroopsEnabled),
        autoPullBackTroops: Boolean(value?.autoPullBackTroops ?? fallback.autoPullBackTroops),
        keepTroops: Array.isArray(value?.keepTroops) ? value.keepTroops.map(normalizeKeepEntry) : [],
        sendToX: toStringValue(value?.sendToX, '0'),
        sendToY: toStringValue(value?.sendToY, '0'),
        openGate: Boolean(value?.openGate)
    }
}

const makeLocation = (key, title, prefix = '') => ({
    key,
    title,
    prefix
})

const LOCATION_GROUPS = [
    {
        title: 'Main Castle',
        helper: 'Configure troop saving and gate behavior for Main',
        locations: [makeLocation('main', 'Main Castle', '')]
    },
    {
        title: 'Outposts',
        helper: 'Configure Outpost 1-3',
        locations: [
            makeLocation('outpost1', 'Outpost 1', 'Outpost1: '),
            makeLocation('outpost2', 'Outpost 2', 'Outpost2: '),
            makeLocation('outpost3', 'Outpost 3', 'Outpost3: ')
        ]
    },
    {
        title: 'Kingdoms',
        helper: 'Configure Ice, Desert, Fire, Storm',
        locations: [
            makeLocation('ice', 'Ice', 'Ice: '),
            makeLocation('desert', 'Desert', 'Desert: '),
            makeLocation('fire', 'Fire', 'Fire: '),
            makeLocation('storm', 'Storm', 'Storm: '),
            makeLocation('capital', 'Capital', 'Capital: '),
            makeLocation('metro', 'Metro', 'Metro: ')
        ]
    }
]

const DEFAULT_CASTLE_DEFENSE_CONFIG = {
    ...DEFAULT_LOCATION_SETTINGS,
    autoPullBackTroops: false,
    locations: Object.fromEntries(
        LOCATION_GROUPS
            .flatMap(group => group.locations)
            .map(location => [location.key, createLocationState()])
    )
}

const PRIMARY_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const LOCATION_SECTION_SX = {
    borderRadius: '18px',
    border: '1px solid rgba(129, 146, 178, 0.14)',
    backgroundColor: 'rgba(16, 25, 41, 0.44)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.02)'
}

const GROUP_SECTION_SX = {
    borderRadius: '22px',
    border: '1px solid rgba(129, 146, 178, 0.18)',
    background: 'linear-gradient(180deg, rgba(21, 33, 55, 0.62) 0%, rgba(18, 29, 48, 0.5) 100%)',
    boxShadow: '0 14px 32px rgba(7, 13, 26, 0.18)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(92, 118, 163, 0.07) 0%, rgba(92, 118, 163, 0) 22%)',
        pointerEvents: 'none'
    }
}

const extractLevelValue = (statLine, fallbackLevel = '') => {
    const match = String(statLine ?? '').match(/Level\s+([0-9]+)/i)
    return (match ? match[1] : '') || toStringValue(fallbackLevel)
}

const buildCatalogEntries = catalog => {
    const entries = []
    const appendEntries = (items, itemType) => {
        for (const item of Array.isArray(items) ? items : []) {
            const id = toStringValue(item?.value).trim()
            if (!id)
                continue

            entries.push({
                key: `${itemType}:${id}`,
                id,
                label: toStringValue(item?.label, id),
                itemType,
                statLine: toStringValue(item?.statLine),
                level: extractLevelValue(item?.statLine, item?.level),
                iconUrl: toStringValue(item?.iconUrl),
                assetKey: toStringValue(item?.assetKey),
                searchText: toStringValue(item?.searchText, `${item?.label ?? ''} ${id}`)
            })
        }
    }

    appendEntries([...(catalog?.attackTroops ?? []), ...(catalog?.defenseTroops ?? [])], 'troop')

    return entries
}

const loadPickerFavorites = () => {
    if (typeof window === 'undefined')
        return []

    try {
        const parsed = JSON.parse(window.localStorage.getItem(PICKER_FAVORITES_KEY) ?? '[]')
        return Array.isArray(parsed) ? parsed.map(value => String(value)).filter(Boolean) : []
    }
    catch {
        return []
    }
}

const savePickerFavorites = favoriteKeys => {
    if (typeof window === 'undefined')
        return

    try {
        window.localStorage.setItem(PICKER_FAVORITES_KEY, JSON.stringify(favoriteKeys))
    }
    catch { }
}

export const normalizeCastleDefenseConfig = pluginState => {
    const source = pluginState?.castleDefenseConfig && typeof pluginState.castleDefenseConfig === 'object' ?
        pluginState.castleDefenseConfig :
        {}
    const legacyAutoPullBackTroops = Boolean(source.autoPullBackTroops ?? pluginState?.autoPullBackTroops)
    const legacyLocationSettings = {
        attackThresholdMinutes: toStringValue(source.attackThresholdMinutes ?? pluginState?.attackThresholdMinutes, DEFAULT_CASTLE_DEFENSE_CONFIG.attackThresholdMinutes),
        troopSavingDurationHours: toStringValue(source.troopSavingDurationHours ?? pluginState?.stationTime, DEFAULT_CASTLE_DEFENSE_CONFIG.troopSavingDurationHours),
        troopAttackThreshold: toStringValue(source.troopAttackThreshold ?? pluginState?.minHitTroopCount, DEFAULT_CASTLE_DEFENSE_CONFIG.troopAttackThreshold),
        minMightPoints: toStringValue(source.minMightPoints ?? pluginState?.minMightPoints, DEFAULT_CASTLE_DEFENSE_CONFIG.minMightPoints),
        minCastleTroopCount: toStringValue(source.minCastleTroopCount ?? pluginState?.minCastleTroopCount, DEFAULT_CASTLE_DEFENSE_CONFIG.minCastleTroopCount),
        useFeathers: Boolean(source.useFeathers ?? pluginState?.useFeathers ?? DEFAULT_CASTLE_DEFENSE_CONFIG.useFeathers),
        useReserveCommander: Boolean(source.useReserveCommander ?? pluginState?.useReserveCommander ?? DEFAULT_CASTLE_DEFENSE_CONFIG.useReserveCommander),
        minimumSendAmount: toStringValue(source.minimumSendAmount ?? pluginState?.minTroopSendCount, DEFAULT_CASTLE_DEFENSE_CONFIG.minimumSendAmount),
        reserveCommanderWhiteList: toStringValue(source.reserveCommanderWhiteList ?? pluginState?.reserveCommanderWhiteList, DEFAULT_CASTLE_DEFENSE_CONFIG.reserveCommanderWhiteList)
    }
    const locations = Object.fromEntries(
        LOCATION_GROUPS
            .flatMap(group => group.locations)
            .map(location => [
                location.key,
                normalizeLocationState(source?.locations?.[location.key], {
                    ...legacyLocationSettings,
                    autoPullBackTroops: legacyAutoPullBackTroops
                })
            ])
    )

    return {
        ...legacyLocationSettings,
        autoPullBackTroops: Object.values(locations).some(location => Boolean(location?.autoPullBackTroops)),
        locations
    }
}

const deriveCastleDefenseFields = config => {
    const mainLocation = config?.locations?.main ?? {}

    return {
        ...config,
        attackThresholdMinutes: toStringValue(mainLocation.attackThresholdMinutes ?? config?.attackThresholdMinutes, DEFAULT_CASTLE_DEFENSE_CONFIG.attackThresholdMinutes),
        troopSavingDurationHours: toStringValue(mainLocation.troopSavingDurationHours ?? config?.troopSavingDurationHours, DEFAULT_CASTLE_DEFENSE_CONFIG.troopSavingDurationHours),
        troopAttackThreshold: toStringValue(mainLocation.troopAttackThreshold ?? config?.troopAttackThreshold, DEFAULT_CASTLE_DEFENSE_CONFIG.troopAttackThreshold),
        minMightPoints: toStringValue(mainLocation.minMightPoints ?? config?.minMightPoints, DEFAULT_CASTLE_DEFENSE_CONFIG.minMightPoints),
        minCastleTroopCount: toStringValue(mainLocation.minCastleTroopCount ?? config?.minCastleTroopCount, DEFAULT_CASTLE_DEFENSE_CONFIG.minCastleTroopCount),
        useFeathers: Boolean(mainLocation.useFeathers ?? config?.useFeathers ?? DEFAULT_CASTLE_DEFENSE_CONFIG.useFeathers),
        minimumSendAmount: toStringValue(mainLocation.minimumSendAmount ?? config?.minimumSendAmount, DEFAULT_CASTLE_DEFENSE_CONFIG.minimumSendAmount),
        autoPullBackTroops: Object.values(config?.locations ?? {}).some(location => Boolean(location?.autoPullBackTroops))
    }
}

function SettingSwitch({ checked, onChange }) {
    return (
        <Switch
            checked={checked}
            onChange={event => onChange(event.target.checked)}
            sx={{
                width: 48,
                height: 29,
                p: 0,
                '& .MuiSwitch-switchBase': {
                    p: '2.5px',
                    '&.Mui-checked': {
                        transform: 'translateX(18px)',
                        color: '#ffffff'
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2f7cff',
                        opacity: 1
                    }
                },
                '& .MuiSwitch-thumb': {
                    width: 24,
                    height: 24,
                    boxShadow: 'none'
                },
                '& .MuiSwitch-track': {
                    opacity: 1,
                    borderRadius: '999px',
                    backgroundColor: 'rgba(111, 123, 143, 0.9)'
                }
            }}
        />
    )
}

function HelperText({ children }) {
    return (
        <Stack direction="row" spacing={0.55} alignItems="flex-start" sx={{ mt: 0.65 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#96a5c0', mt: '2px' }} />
            <Typography sx={{ color: '#96a5c0', fontSize: '0.9rem', lineHeight: 1.35 }}>
                {children}
            </Typography>
        </Stack>
    )
}

function PickerFilterButton({ icon, label, count, active, onClick }) {
    return (
        <Button
            variant="outlined"
            onClick={onClick}
            startIcon={icon}
            sx={{
                minHeight: 36,
                px: 1.35,
                borderRadius: '10px',
                borderColor: active ? 'rgba(112, 136, 174, 0.56)' : 'rgba(112, 136, 174, 0.34)',
                backgroundColor: active ? 'rgba(74, 91, 120, 0.72)' : 'rgba(42, 54, 78, 0.56)',
                color: '#eef4ff',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.96rem',
                gap: 0.8
            }}
        >
            <Box component="span">{label}</Box>
            <Box
                component="span"
                sx={{
                    px: 0.75,
                    minWidth: 34,
                    minHeight: 24,
                    borderRadius: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: '#aebad3',
                    fontWeight: 800,
                    fontSize: '0.95rem'
                }}
            >
                {count}
            </Box>
        </Button>
    )
}

function TroopToolPickerDialog({ open, entries, onClose, onSelect }) {
    const [query, setQuery] = React.useState('')
    const [filterValue, setFilterValue] = React.useState('all')
    const [favoritesOnly, setFavoritesOnly] = React.useState(false)
    const [favoriteKeys, setFavoriteKeys] = React.useState(() => loadPickerFavorites())
    const favoriteKeySet = React.useMemo(() => new Set(favoriteKeys), [favoriteKeys])

    React.useEffect(() => {
        if (!open)
            return

        setQuery('')
        setFilterValue('all')
        setFavoritesOnly(false)
    }, [open])

    React.useEffect(() => {
        savePickerFavorites(favoriteKeys)
    }, [favoriteKeys])

    const counts = React.useMemo(() => ({
        all: entries.length,
        troops: entries.filter(entry => entry.itemType === 'troop').length
    }), [entries])

    const filteredEntries = React.useMemo(() => {
        const normalizedQuery = normalizeSearchText(query)

        return entries
            .filter(entry => {
                if (filterValue === 'troops' && entry.itemType !== 'troop')
                    return false
                if (favoritesOnly && !favoriteKeySet.has(entry.key))
                    return false
                if (!normalizedQuery)
                    return true

                return normalizeSearchText(`${entry.label} ${entry.id} ${entry.searchText}`).includes(normalizedQuery)
            })
            .sort((left, right) => {
                const leftFavorite = favoriteKeySet.has(left.key)
                const rightFavorite = favoriteKeySet.has(right.key)
                if (leftFavorite !== rightFavorite)
                    return leftFavorite ? -1 : 1

                return String(left.label ?? '').localeCompare(String(right.label ?? ''))
            })
    }, [entries, favoriteKeySet, favoritesOnly, filterValue, query])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 20px), 566px)',
                    maxWidth: '566px',
                    minHeight: '724px',
                    maxHeight: 'min(calc(100vh - 20px), 724px)',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: 'linear-gradient(180deg, rgba(39,51,74,0.98) 0%, rgba(31,43,66,0.98) 100%)',
                    border: '1px solid rgba(122, 143, 180, 0.32)',
                    boxShadow: '0 26px 60px rgba(0,0,0,0.42)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(3, 8, 18, 0.68)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.2, pt: 2.05, pb: 1.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <SearchRoundedIcon sx={{ fontSize: 28, color: '#f4f7ff' }} />
                        <Typography sx={{ fontSize: '1.16rem', fontWeight: 800, color: '#f5f7ff' }}>
                            Troop Picker
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ color: '#9aa7c2', width: 30, height: 30 }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 2.2, pt: 0, pb: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Stack direction="row" spacing={1.1} sx={{ mb: 1.5 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by name or ID..."
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                minHeight: 42,
                                borderRadius: '10px',
                                backgroundColor: 'rgba(28, 41, 65, 0.9)',
                                '& fieldset': {
                                    borderColor: 'rgba(57, 121, 248, 0.95)',
                                    borderWidth: '2px'
                                }
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: '#a8b4cd', fontSize: 24 }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={favoriteKeySet.size > 0 || favoritesOnly ? <FavoriteRoundedIcon sx={{ color: favoritesOnly ? '#ff8ea0' : '#ffffff' }} /> : <FavoriteBorderRoundedIcon />}
                        onClick={() => setFavoritesOnly(current => !current)}
                        sx={{
                            minWidth: 130,
                            minHeight: 42,
                            borderRadius: '10px',
                            borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.72)' : 'rgba(112, 136, 174, 0.34)',
                            backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.14)' : 'rgba(42, 54, 78, 0.56)',
                            color: '#f3f6ff',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.96rem'
                        }}
                    >
                        Favorites
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CloseRoundedIcon />}
                        onClick={() => {
                            setQuery('')
                            setFilterValue('all')
                            setFavoritesOnly(false)
                        }}
                        sx={{
                            minWidth: 110,
                            minHeight: 42,
                            borderRadius: '10px',
                            backgroundColor: '#ff3345',
                            textTransform: 'none',
                            fontWeight: 800,
                            fontSize: '0.96rem',
                            boxShadow: 'none'
                        }}
                    >
                        Clear
                    </Button>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.55 }}>
                    <Stack direction="row" spacing={0.85}>
                        <PickerFilterButton icon={<FilterAltRoundedIcon sx={{ fontSize: 19 }} />} label="All" count={counts.all} active={filterValue === 'all'} onClick={() => setFilterValue('all')} />
                        <PickerFilterButton icon={<GroupsRoundedIcon sx={{ fontSize: 19 }} />} label="Troops" count={counts.troops} active={filterValue === 'troops'} onClick={() => setFilterValue('troops')} />
                    </Stack>
                    <Typography sx={{ color: '#b3bfd7', fontWeight: 700, fontSize: '0.98rem' }}>
                        {`${filteredEntries.length} results`}
                    </Typography>
                </Stack>

                <Box sx={{ flex: 1, minHeight: 0, borderRadius: '12px', border: '1px solid rgba(123, 147, 186, 0.28)', backgroundColor: 'rgba(31, 43, 67, 0.92)', overflowY: 'auto' }}>
                    {filteredEntries.length > 0 ? filteredEntries.map((entry, index) => {
                        const isFavorite = favoriteKeySet.has(entry.key)
                        return (
                            <Box
                                key={entry.key}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    onSelect?.(entry)
                                    onClose?.()
                                }}
                                sx={{
                                    px: 2.15,
                                    py: 1.6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.2,
                                    cursor: 'pointer',
                                    borderBottom: index < filteredEntries.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                                    <UnitIconFrame
                                        iconUrl={entry.iconUrl}
                                        itemType={entry.itemType}
                                        level={entry.itemType === 'troop' ? resolveUnitLevelBadge(entry) : ''}
                                        size={52}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.9} sx={{ mb: 0.6 }}>
                                            <Typography sx={{ color: '#5ea3ff', fontWeight: 800, fontSize: '1.01rem' }}>
                                                {`#${index + 1}`}
                                            </Typography>
                                            <Box sx={{ px: 0.95, minHeight: 28, borderRadius: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(130, 147, 177, 0.45)', color: '#d6deec', fontWeight: 700, fontSize: '0.94rem' }}>
                                                {entry.itemType === 'tool' ? 'Tool' : 'Troop'}
                                            </Box>
                                        </Stack>
                                        <Typography sx={{ color: '#f5f7ff', fontWeight: 700, fontSize: '1.14rem', lineHeight: 1.18 }}>
                                            {entry.label}
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconButton
                                    onClick={event => {
                                        event.stopPropagation()
                                        setFavoriteKeys(currentKeys =>
                                            currentKeys.includes(entry.key) ?
                                                currentKeys.filter(currentKey => currentKey !== entry.key) :
                                                [...currentKeys, entry.key]
                                        )
                                    }}
                                    sx={{ color: isFavorite ? '#ff6e82' : '#9aa6bf', width: 34, height: 34 }}
                                >
                                    {isFavorite ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                </IconButton>
                            </Box>
                        )
                    }) : (
                        <Box sx={{ px: 2.2, py: 3.2 }}>
                            <Typography sx={{ color: '#b1bdd5', fontWeight: 600 }}>
                                No results found
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ mt: 1.45, px: 2.2, py: 1.45, mx: -2.2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <Typography sx={{ color: '#7f8aa3', fontSize: '0.94rem' }}>
                        Click to select | Favorite to bookmark | Type an ID manually in the input field
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function CastleDefenseLocationBlock({ location, value, onChange, onAddKeepTroop, onOpenPicker, showHeading = true, catalogEntriesByKey = new Map() }) {
    const keepTroops = Array.isArray(value?.keepTroops) ? value.keepTroops : []
    const prefix = location.prefix
    const sendLabel = `${prefix}Send Troops Enabled`.trim()
    const autoPullBackLabel = `${prefix}Auto Pull Back Troops`.trim()
    const keepLabel = prefix === '' ? 'Keep Troops [troopId, amount]' : `${prefix}Keep Troops [id, amt]`.trim()
    const sendXLabel = `${prefix}Send To X`.trim()
    const sendYLabel = `${prefix}Send To Y`.trim()
    const gateLabel = `${prefix}Open Gate`.trim()
    const settingLabel = label => `${prefix}${label}`.trim()
    const attackThresholdLabel = settingLabel('Attack Threshold (minutes)')
    const troopSavingDurationLabel = settingLabel('Troop Saving Duration (hours)')
    const troopAttackThresholdLabel = settingLabel('Troop Attack Threshold')
    const minMightPointsLabel = settingLabel('Minimum Might Points')
    const minCastleTroopCountLabel = settingLabel('Min Castle Troop Count')
    const minimumSendAmountLabel = settingLabel('Minimum Send Amount')
    const extraCommanderLabel = settingLabel('Extra Commander')
    const useFeathersLabel = settingLabel('Use Feathers')
    const sendHelper =
        prefix === '' ? 'Enable troop sending from Main' :
            prefix === 'Outpost1: ' ? 'Enable troop sending from Outpost 1' :
                prefix === 'Outpost2: ' ? 'Enable troop sending from Outpost 2' :
                    prefix === 'Outpost3: ' ? 'Enable troop sending from Outpost 3' :
                        ''
    const autoPullBackHelper =
        prefix === '' ? 'Pull saved troops back automatically when Main is safe again' :
            'Pull saved troops back automatically when this location is safe again'
    const keepHelper = prefix === '' ? 'Pairs of [troopId, amount] to keep at Main' : 'Pairs of [troopId, amount] to keep'
    const xHelper = prefix === '' ? 'Target castle X coordinate' : ''
    const yHelper = prefix === '' ? 'Target castle Y coordinate' : ''
    const gateHelper = prefix === '' ? 'Open the gate when under attack' : ''
    const keepCountLabel = `${keepLabel} (${keepTroops.length} ${keepTroops.length === 1 ? 'item' : 'items'})`

    const handleEntryChange = (index, updater) => {
        onChange({
            ...value,
            keepTroops: keepTroops.map((entry, currentIndex) =>
                currentIndex === index ? updater(entry) : entry
            )
        })
    }

    return (
        <Box sx={{ ...LOCATION_SECTION_SX, p: { xs: 1.5, md: 1.9 } }}>
            <Stack spacing={2.1}>
                {showHeading ? (
                    <Stack direction="row" alignItems="center" spacing={1.2}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#f4f7ff' }}>
                            {location.title}
                        </Typography>
                        <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.12)' }} />
                    </Stack>
                ) : null}

                <Box sx={{ display: 'grid', gap: 2.2, gridTemplateColumns: { xs: '1fr', xl: '1.02fr 1fr' } }}>
                    <Stack spacing={1.55}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.1}>
                                <SettingSwitch checked={Boolean(value?.sendTroopsEnabled)} onChange={nextValue => onChange({ ...value, sendTroopsEnabled: nextValue })} />
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 700, color: '#f5f7ff' }}>
                                    {sendLabel}
                                </Typography>
                            </Stack>
                            {sendHelper ? <HelperText>{sendHelper}</HelperText> : null}
                        </Box>

                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.1}>
                                <SettingSwitch checked={Boolean(value?.autoPullBackTroops)} onChange={nextValue => onChange({ ...value, autoPullBackTroops: nextValue })} />
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 700, color: '#f5f7ff' }}>
                                    {autoPullBackLabel}
                                </Typography>
                            </Stack>
                            <HelperText>{autoPullBackHelper}</HelperText>
                        </Box>

                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.1}>
                                <SettingSwitch checked={Boolean(value?.useFeathers)} onChange={nextValue => onChange({ ...value, useFeathers: nextValue })} />
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 700, color: '#f5f7ff' }}>
                                    {useFeathersLabel}
                                </Typography>
                            </Stack>
                            <HelperText>Use feathers when sending troops from this location</HelperText>
                        </Box>

                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.1}>
                                <SettingSwitch checked={Boolean(value?.useReserveCommander)} onChange={nextValue => onChange({ ...value, useReserveCommander: nextValue })} />
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 700, color: '#f5f7ff' }}>
                                    {extraCommanderLabel}
                                </Typography>
                            </Stack>
                            <HelperText>Prioritize the in-game Extra Commander. When disabled, use Commander Allocation.</HelperText>
                        </Box>

                        <Box sx={{ display: 'grid', gap: 1.45, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                            <Box>
                                <Typography sx={{ mb: 0.65, fontSize: '0.99rem', fontWeight: 700, color: '#f5f7ff' }}>{attackThresholdLabel}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={toStringValue(value?.attackThresholdMinutes, DEFAULT_LOCATION_SETTINGS.attackThresholdMinutes)}
                                    onChange={event => onChange({ ...value, attackThresholdMinutes: event.target.value })}
                                    sx={PRIMARY_INPUT_SX}
                                />
                                <HelperText>Act when an incoming attack lands within this many minutes</HelperText>
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 0.65, fontSize: '0.99rem', fontWeight: 700, color: '#f5f7ff' }}>{troopSavingDurationLabel}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={toStringValue(value?.troopSavingDurationHours, DEFAULT_LOCATION_SETTINGS.troopSavingDurationHours)}
                                    onChange={event => onChange({ ...value, troopSavingDurationHours: event.target.value })}
                                    sx={PRIMARY_INPUT_SX}
                                />
                                <HelperText>Duration to keep troops saved away from this location</HelperText>
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 0.65, fontSize: '0.99rem', fontWeight: 700, color: '#f5f7ff' }}>{troopAttackThresholdLabel}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={toStringValue(value?.troopAttackThreshold, DEFAULT_LOCATION_SETTINGS.troopAttackThreshold)}
                                    onChange={event => onChange({ ...value, troopAttackThreshold: event.target.value })}
                                    sx={PRIMARY_INPUT_SX}
                                />
                                <HelperText>Minimum enemy troop count to trigger this location</HelperText>
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 0.65, fontSize: '0.99rem', fontWeight: 700, color: '#f5f7ff' }}>{minMightPointsLabel}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={toStringValue(value?.minMightPoints, DEFAULT_LOCATION_SETTINGS.minMightPoints)}
                                    onChange={event => onChange({ ...value, minMightPoints: event.target.value })}
                                    sx={PRIMARY_INPUT_SX}
                                />
                                <HelperText>Ignore attackers below this might threshold</HelperText>
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 0.65, fontSize: '0.99rem', fontWeight: 700, color: '#f5f7ff' }}>{minCastleTroopCountLabel}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={toStringValue(value?.minCastleTroopCount, DEFAULT_LOCATION_SETTINGS.minCastleTroopCount)}
                                    onChange={event => onChange({ ...value, minCastleTroopCount: event.target.value })}
                                    sx={PRIMARY_INPUT_SX}
                                />
                                <HelperText>Do not flee if this location has fewer troops</HelperText>
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 0.65, fontSize: '0.99rem', fontWeight: 700, color: '#f5f7ff' }}>{minimumSendAmountLabel}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={toStringValue(value?.minimumSendAmount, DEFAULT_LOCATION_SETTINGS.minimumSendAmount)}
                                    onChange={event => onChange({ ...value, minimumSendAmount: event.target.value })}
                                    sx={PRIMARY_INPUT_SX}
                                />
                                <HelperText>Minimum amount per troop stack for this location</HelperText>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gap: 1.45, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                            <Box>
                                <Typography sx={{ mb: 0.65, fontSize: '0.99rem', fontWeight: 700, color: '#f5f7ff' }}>{sendXLabel}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={toStringValue(value?.sendToX, '0')}
                                    onChange={event => onChange({ ...value, sendToX: event.target.value })}
                                    sx={PRIMARY_INPUT_SX}
                                />
                                {xHelper ? <HelperText>{xHelper}</HelperText> : null}
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 0.65, fontSize: '0.99rem', fontWeight: 700, color: '#f5f7ff' }}>{sendYLabel}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={toStringValue(value?.sendToY, '0')}
                                    onChange={event => onChange({ ...value, sendToY: event.target.value })}
                                    sx={PRIMARY_INPUT_SX}
                                />
                                {yHelper ? <HelperText>{yHelper}</HelperText> : null}
                            </Box>
                        </Box>

                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.1}>
                                <SettingSwitch checked={Boolean(value?.openGate)} onChange={nextValue => onChange({ ...value, openGate: nextValue })} />
                                <Typography sx={{ fontSize: '1.02rem', fontWeight: 700, color: '#f5f7ff' }}>
                                    {gateLabel}
                                </Typography>
                            </Stack>
                            {gateHelper ? <HelperText>{gateHelper}</HelperText> : null}
                        </Box>
                    </Stack>

                    <Stack spacing={1.15}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography sx={{ fontSize: '0.99rem', fontWeight: 700, color: '#f5f7ff' }}>{keepLabel}</Typography>
                                <Typography sx={{ mt: 0.6, fontSize: '0.98rem', fontWeight: 700, color: '#d8e2f3' }}>
                                    {keepCountLabel}
                                </Typography>
                            </Box>
                            <IconButton onClick={onAddKeepTroop} sx={{ width: 34, height: 34, borderRadius: '10px', border: '1px solid rgba(130, 147, 177, 0.36)', color: '#eff4ff' }}>
                                <AddRoundedIcon />
                            </IconButton>
                        </Stack>

                        {keepTroops.map((entry, index) => {
                            const resolvedEntry = catalogEntriesByKey.get(`${entry?.itemType}:${entry?.id}`) ?? entry

                            return (
                            <Box key={`${location.key}-keep-${index}`} sx={{ p: 1.1, borderRadius: '6px', backgroundColor: 'rgba(66, 84, 113, 0.62)' }}>
                                <Stack direction="row" spacing={1.15} alignItems="flex-start">
                                    <UnitIconFrame
                                        iconUrl={resolvedEntry?.iconUrl}
                                        itemType={resolvedEntry?.itemType ?? 'troop'}
                                        level={resolvedEntry?.itemType === 'troop' ? resolveUnitLevelBadge(resolvedEntry) : ''}
                                        amount={entry?.amount ? `x${entry.amount}` : ''}
                                        size={44}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={entry?.id ? `${entry.id}: ${resolvedEntry?.label || 'Unknown'}` : ''}
                                            placeholder={keepLabel}
                                            onClick={() => onOpenPicker(index)}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    backgroundColor: 'rgba(38, 52, 79, 0.72)'
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '0.99rem',
                                                    fontWeight: 700,
                                                    color: '#f5f7ff',
                                                    cursor: 'pointer'
                                                }
                                            }}
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={event => {
                                                                event.stopPropagation()
                                                                onOpenPicker(index)
                                                            }}
                                                            sx={{ width: 38, height: 34, borderRadius: '10px', border: '1px solid rgba(130, 147, 177, 0.36)', color: '#eff4ff' }}
                                                        >
                                                            <SearchRoundedIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <Stack direction="row" spacing={1} sx={{ mt: 0.8 }}>
                                            {resolvedEntry?.itemType ? <Box sx={{ px: 0.95, minHeight: 24, borderRadius: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(130, 147, 177, 0.24)', color: '#e0e8f7', fontSize: '0.9rem', fontWeight: 700 }}>{resolvedEntry.itemType === 'tool' ? 'Tool' : 'Troop'}</Box> : null}
                                            {resolvedEntry?.itemType === 'troop' && resolvedEntry?.level ? <Typography sx={{ color: '#b7c3da', fontSize: '0.95rem' }}>{`Level ${resolvedEntry.level}`}</Typography> : null}
                                        </Stack>
                                    </Box>

                                    <TextField
                                        size="small"
                                        value={toStringValue(entry?.amount)}
                                        onChange={event => handleEntryChange(index, currentEntry => ({ ...currentEntry, amount: event.target.value }))}
                                        sx={{
                                            width: 104,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                                backgroundColor: 'rgba(38, 52, 79, 0.72)'
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.99rem',
                                                fontWeight: 700,
                                                color: '#f5f7ff'
                                            }
                                        }}
                                    />

                                    <IconButton
                                        onClick={() => onChange({ ...value, keepTroops: keepTroops.filter((_, currentIndex) => currentIndex !== index) })}
                                        sx={{ width: 42, height: 42, borderRadius: '10px', backgroundColor: '#ff3345', color: '#ffffff', '&:hover': { backgroundColor: '#ff4858' } }}
                                    >
                                        <RemoveRoundedIcon />
                                    </IconButton>
                                </Stack>
                            </Box>
                        )})}

                        <HelperText>{keepHelper}</HelperText>
                    </Stack>
                </Box>
            </Stack>
        </Box>
    )
}

export default function CastleDefenseSettingsPanel({
    pluginKey = 'flee',
    userPlugins,
    onChange,
    appLanguage = 'en'
}) {
    const config = React.useMemo(
        () => normalizeCastleDefenseConfig(userPlugins?.[pluginKey] ?? {}),
        [pluginKey, userPlugins]
    )
    const [pickerEntries, setPickerEntries] = React.useState([])
    const [pickerContext, setPickerContext] = React.useState(null)
    const pickerEntriesByKey = React.useMemo(
        () => new Map(pickerEntries.map(entry => [`${entry.itemType}:${entry.id}`, entry])),
        [pickerEntries]
    )

    React.useEffect(() => {
        let cancelled = false

        fetch(`/attack-template-catalog.json?language=${encodeURIComponent(appLanguage || 'en')}`)
            .then(response => response.json())
            .then(payload => {
                if (!cancelled)
                    setPickerEntries(buildCatalogEntries(payload))
            })
            .catch(error => console.warn('Failed to load troop/tool picker catalog', error))

        return () => {
            cancelled = true
        }
    }, [appLanguage])

    const updatePlugin = React.useCallback(updater => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[pluginKey] ??= {}
        const currentState = normalizeCastleDefenseConfig(nextPlugins[pluginKey])
        const nextConfig = deriveCastleDefenseFields(updater(currentState))

        nextPlugins[pluginKey].castleDefenseConfig = nextConfig
        nextPlugins[pluginKey].stationTime = nextConfig.troopSavingDurationHours
        nextPlugins[pluginKey].minHitTroopCount = nextConfig.troopAttackThreshold
        nextPlugins[pluginKey].minMightPoints = nextConfig.minMightPoints
        nextPlugins[pluginKey].minCastleTroopCount = nextConfig.minCastleTroopCount
        nextPlugins[pluginKey].minTroopSendCount = nextConfig.minimumSendAmount
        nextPlugins[pluginKey].attackThresholdMinutes = nextConfig.attackThresholdMinutes
        nextPlugins[pluginKey].useFeathers = nextConfig.useFeathers
        nextPlugins[pluginKey].autoPullBackTroops = nextConfig.autoPullBackTroops
        onChange(nextPlugins)
    }, [onChange, pluginKey, userPlugins])

    const updateLocation = (locationKey, nextValue) => {
        updatePlugin(currentState => ({
            ...currentState,
            locations: {
                ...currentState.locations,
                [locationKey]: nextValue
            }
        }))
    }

    const handlePickerSelect = entry => {
        if (!pickerContext)
            return

        updatePlugin(currentState => {
            const nextLocations = cloneValue(currentState.locations)
            const nextEntries = [...(nextLocations[pickerContext.locationKey]?.keepTroops ?? [])]
            nextEntries[pickerContext.entryIndex] = {
                ...createKeepEntry(),
                ...nextEntries[pickerContext.entryIndex],
                id: entry.id,
                itemType: entry.itemType,
                label: entry.label,
                statLine: entry.statLine,
                level: entry.level,
                iconUrl: entry.iconUrl,
                assetKey: entry.assetKey
            }
            nextLocations[pickerContext.locationKey] = {
                ...nextLocations[pickerContext.locationKey],
                keepTroops: nextEntries
            }
            return {
                ...currentState,
                locations: nextLocations
            }
        })

        setPickerContext(null)
    }

    return (
        <>
            <Stack spacing={3.2}>
                {LOCATION_GROUPS.map(group => (
                    <Box key={group.title} sx={{ ...GROUP_SECTION_SX, p: { xs: 1.7, md: 2.25 } }}>
                        <Stack spacing={1.6}>
                            <Stack direction="row" alignItems="center" spacing={1.2}>
                                <Typography sx={{ fontSize: '1.18rem', fontWeight: 800, color: '#f4f7ff' }}>{group.title}</Typography>
                                <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.12)' }} />
                            </Stack>
                            <HelperText>{group.helper}</HelperText>

                            {group.locations.map(location => (
                                <CastleDefenseLocationBlock
                                    key={location.key}
                                    location={location}
                                    value={config.locations[location.key]}
                                    showHeading={group.locations.length > 1}
                                    onChange={nextValue => updateLocation(location.key, nextValue)}
                                    onAddKeepTroop={() => updateLocation(location.key, { ...config.locations[location.key], keepTroops: [...(config.locations[location.key]?.keepTroops ?? []), createKeepEntry()] })}
                                    onOpenPicker={entryIndex => setPickerContext({ locationKey: location.key, entryIndex })}
                                    catalogEntriesByKey={pickerEntriesByKey}
                                />
                            ))}
                        </Stack>
                    </Box>
                ))}
            </Stack>

            <TroopToolPickerDialog open={Boolean(pickerContext)} entries={pickerEntries} onClose={() => setPickerContext(null)} onSelect={handlePickerSelect} />
        </>
    )
}
